import { generateExampleFromSchema } from './generate-example-from-schema';
import type { OpenAPISpec, OpenAPIOperation, OpenAPISchemaProperty } from '../types/types';

/**
 * Generates a cURL command with proper headers and request body
 * @param method - HTTP method (GET, POST, PUT, etc.)
 * @param url - The full URL for the request
 * @param operation - OpenAPI operation object containing parameters, security, etc.
 * @param spec - Full OpenAPI specification for security scheme lookup
 * @returns Formatted cURL command string
 */
export const generateCurlCommand = (
  method: string,
  url: string,
  operation: OpenAPIOperation,
  spec: OpenAPISpec,
): string => {
  const headers: string[] = [];

  if (operation.parameters) {
    operation.parameters.forEach((param) => {
      if (param.in === 'header') {
        const exampleValue = param.schema?.default || `<${param.name}>`;
        headers.push(`-H "${param.name}: ${exampleValue}"`);
      }
    });
  }

  const securityRequirements = operation.security || spec.security;
  if (securityRequirements && spec.components?.securitySchemes) {
    securityRequirements.forEach((requirement) => {
      Object.keys(requirement).forEach((schemeName) => {
        const scheme = spec.components?.securitySchemes?.[schemeName];
        if (scheme) {
          if (scheme.type === 'apiKey' && scheme.in === 'header') {
            headers.push(`-H "${scheme.name}: <YOUR_API_KEY>"`);
          } else if (scheme.type === 'http' && scheme.scheme === 'bearer') {
            headers.push(`-H "Authorization: Bearer <YOUR_TOKEN>"`);
          } else if (scheme.type === 'http' && scheme.scheme === 'basic') {
            headers.push(`-H "Authorization: Basic <YOUR_CREDENTIALS>"`);
          }
        }
      });
    });
  }

  let requestBodyData: string | null = null;
  if (operation.requestBody?.content) {
    const contentTypes = Object.keys(operation.requestBody.content);
    if (contentTypes.length > 0) {
      const contentType = contentTypes[0];
      headers.push(`-H "Content-Type: ${contentType}"`);

      const mediaType = operation.requestBody.content[contentType];
      if (mediaType?.schema) {
        const exampleData = generateExampleFromSchema(mediaType.schema as OpenAPISchemaProperty);
        if (contentType === 'application/json') {
          requestBodyData = JSON.stringify(exampleData, null, 2);
        } else {
          requestBodyData = JSON.stringify(exampleData);
        }
      }
    }
  }

  let curlCommand = `curl -X ${method.toUpperCase()}`;
  if (headers.length > 0) {
    curlCommand += ` \\\n  ${headers.join(' \\\n  ')}`;
  }
  if (requestBodyData) {
    curlCommand += ` \\\n  -d '${requestBodyData}'`;
  }
  curlCommand += ` \\\n  "${url}"`;

  return curlCommand;
};
